import datetimeDecorator from 'src/decorators/datetime.decorator';
import {
  ConvenienceDispatchFixedRouteDetailEntity,
  ConvenienceDispatchFixedRouteStopEntity,
  ConvenienceDispatchFixedRouteVehicleEntity,
} from 'src/entities/ConvenienceDispatchFixedRoute.entity';
import { PlanningsDeliveryWithLegRunEntity, PlanningsLegRunOperationEntity } from 'src/entities/PlanningsDelivery.entity';
import { PlanningsDriverEntity } from 'src/entities/PlanningsDriver.entity';
import { PlanningsTruckEntity } from 'src/entities/PlanningsTruck.entity';
import { ShipmentOrderLegRunForPlanEntity } from 'src/entities/ShipmentOrderLegRun.entity';

/**
 * 中央ボードの 1 カード単位。地点ごとにまとめた表示用 VM。
 * V3Plans の Place grouping と同じ考え方:
 *   連続した同地点・同 action の operations を 1 card にまとめる。
 */
export type VehicleRowCardVM = {
  /**
   * - loading / unloading: 実 operation に基づくカード
   * - no_delivery: 固定ルートに存在するが legRun が今日存在しない（本日なし）
   * - template: 固定ルートに存在しており legRun は未割当（仮カード、薄く表示）
   */
  kind: 'loading' | 'unloading' | 'no_delivery' | 'template';
  /**
   * 固定ルート由来の template / no_delivery カードについて、元の stop が積地か降地かを保持する。
   * (kind=loading/unloading は実 operation 由来なので未設定)。
   * placeLegRunInRow で「ドロップ時に土台の積地カードを除去する」判定に使う。
   */
  stopType?: 'loading' | 'unloading';
  /** grouping key: lat-lng ベース (位置情報未取得時はフォールバック文字列) */
  placeKey: string;
  placeName: string;
  /** 代表 arrivalAt の HH:mm 文字列。no_delivery は null */
  timeLabel: string | null;
  /** このカードに含まれる legRun の item_count 合計 */
  itemCount: number;
  operations: PlanningsLegRunOperationEntity[];
  legRuns: ShipmentOrderLegRunForPlanEntity[];
  /** 固定ルート外の operation が 1 件でも含まれる (Board 側で fixedRouteSelected と AND して使う) */
  isRouteDeviation: boolean;
  isAutoAssigned: boolean;
};

export type VehicleRowVM = {
  deliveryId: number;
  truckId: number;
  rotationNumber: number;
  totalRotations: number;
  driverColor: string;
  totalItemCount: number;
  /** 積地・降地・本日なし をまとめた表示用カードリスト。Board はこれを描画する */
  cards: VehicleRowCardVM[];
  /** fixed route 専用の合成行。実在する delivery ではないため選択・集計対象から除外する */
  isPlaceholderRow: boolean;
  /** ローカルで「回転追加」ボタン経由で挿入された仮想 row。
   *  isPlaceholderRow=true と併用するが、同一 truck の代表 delivery にぶら下がるので
   *  「全選択」など UI 操作の対象には含める (isPlaceholderRow=true && isExtraRotation=false の
   *   FR 合成行とは扱いを分ける)。 */
  isExtraRotation?: boolean;
};

/**
 * operation と対応 legRun から grouping キーを生成する。
 * V3Plans と同じく lat-lng ベース。位置情報が無い場合は地点名にフォールバック。
 */
function buildPlaceKey(op: PlanningsLegRunOperationEntity, legRun: ShipmentOrderLegRunForPlanEntity | undefined): string {
  if (!legRun) return `op-${op.id}`;
  if (op.action === '積') {
    const { loading_lat, loading_lng } = legRun;
    if (loading_lat != null && loading_lng != null) return `${loading_lat}-${loading_lng}`;
    return legRun.loading_name || `op-${op.id}`;
  }
  const { unloading_lat, unloading_lng } = legRun;
  if (unloading_lat != null && unloading_lng != null) return `${unloading_lat}-${unloading_lng}`;
  return legRun.unloading_name || `op-${op.id}`;
}

function buildPlaceName(op: PlanningsLegRunOperationEntity, legRun: ShipmentOrderLegRunForPlanEntity | undefined): string {
  if (!legRun) return '—';
  return op.action === '積' ? legRun.loading_name : legRun.unloading_name;
}

/**
 * 固定ルートの stop 用の place key を作る。
 * 同じ「論理的な地点」(同一 name + 同一 lat/lng) は同じ placeKey を返し、
 * mergeOrPushExtra で隣接カードがマージできるようにする。
 * 名前が違う場合は座標が同じでも別 placeKey にする（store_name 単位で識別）。
 *
 * 同 rotation 内に同名 stop が非連続で複数現れたケース (例: A → B → A の往復) は
 * 同じ placeKey で 2 カード生成されるので、React key 側で cardIndex を加えて
 * 一意化する（buildDeliveryCard / noDeliveryCardId 参照）。
 */
function buildFrPlaceKey(
  stopType: 'loading' | 'unloading',
  name: string,
  matchingLegRuns: ShipmentOrderLegRunForPlanEntity[],
): string {
  for (const lr of matchingLegRuns) {
    if (stopType === 'loading' && lr.loading_lat != null && lr.loading_lng != null) {
      return `${stopType}-${name}-${lr.loading_lat}-${lr.loading_lng}`;
    }
    if (stopType === 'unloading' && lr.unloading_lat != null && lr.unloading_lng != null) {
      return `${stopType}-${name}-${lr.unloading_lat}-${lr.unloading_lng}`;
    }
  }
  return `${stopType}-${name}`;
}

/**
 * row 全体で (kind, placeKey) が一致するカードがあれば集約する (連続要件なし)。
 * 同一拠点 (lat/lng 同一) は表記ゆれの name 違いを跨いでも 1 カードに揃える。
 * V3Plans (地図画面) のクラスタリングと同じ粒度に合わせる狙い。
 * 仮カードは legRun を重複排除しつつ追加集約、itemCount を再計算する。
 */
function mergeOrPushExtra(cards: VehicleRowCardVM[], next: VehicleRowCardVM): void {
  const existingIdx = cards.findIndex((c) => c.kind === next.kind && c.placeKey === next.placeKey);
  if (existingIdx >= 0) {
    const existing = cards[existingIdx];
    const seenIds = new Set<number | undefined>(existing.legRuns.map((lr) => lr.id));
    for (const lr of next.legRuns) {
      if (!seenIds.has(lr.id)) {
        existing.legRuns.push(lr);
        seenIds.add(lr.id);
      }
    }
    existing.itemCount = existing.legRuns.reduce((sum, lr) => sum + (lr.item_count ?? 0), 0);
    return;
  }
  cards.push(next);
}

/**
 * 到着順にソート済みの operations を地点単位の VehicleRowCardVM[] にまとめる。
 * row 全体で同地点 (kind + placeKey が一致) の operations は 1 card にマージされる。
 * V3Plans (地図画面) と同じく lat/lng 同一を 1 拠点とみなし、表記ゆれの name 違いも統合する。
 */
function groupOpsToCards(
  sortedOps: PlanningsLegRunOperationEntity[],
  legRunMap: Map<number, ShipmentOrderLegRunForPlanEntity>
): VehicleRowCardVM[] {
  const cards: VehicleRowCardVM[] = [];
  const indexByKey = new Map<string, number>();

  for (const op of sortedOps) {
    const legRun = legRunMap.get(op.shipmentOrderLegRunId);
    const key = buildPlaceKey(op, legRun);
    const kind: 'loading' | 'unloading' = op.action === '積' ? 'loading' : 'unloading';
    const cacheKey = `${kind}-${key}`;
    const existingIdx = indexByKey.get(cacheKey);

    if (existingIdx != null) {
      // 同 row 内・同地点・同 action は (連続でなくても) 既存カードにマージする
      const existing = cards[existingIdx];
      existing.operations.push(op);
      if (legRun) existing.legRuns.push(legRun);
      existing.itemCount += legRun?.item_count ?? 0;
      if (!op.fixed) existing.isRouteDeviation = true;
      if (!op.manualPlanning && op.algorithmRequestId > 0) existing.isAutoAssigned = true;
    } else {
      indexByKey.set(cacheKey, cards.length);
      cards.push({
        kind,
        placeKey: key,
        placeName: buildPlaceName(op, legRun),
        timeLabel: datetimeDecorator.toHourMinutes(new Date(op.arrivalAt)) ?? null,
        itemCount: legRun?.item_count ?? 0,
        operations: [op],
        legRuns: legRun ? [legRun] : [],
        isRouteDeviation: !op.fixed,
        isAutoAssigned: !op.manualPlanning && op.algorithmRequestId > 0,
      });
    }
  }

  return cards;
}

/**
 * deliveries と fixedRouteDetail を合成して VehicleRowVM[] を構築する。
 *
 * ## row の単位
 * `delivery` ではなく `truckId + cycle` が 1 row。
 * 1 delivery の中に cycle=1 と cycle=2 が混在する場合は 2 row を生成する。
 *
 * ## 生成フロー
 * 1. 各 delivery の operations を cycle ごとに分解し、row 候補を作る
 * 2. fixed route detail から row 候補を作る（truckId, rotationNo）
 * 3. (truckId, rotationNumber) の union を取る
 * 4. 実配送 row を構築する（その cycle の operations だけで cards / legRunMap を作る）
 * 5. placeholder row を補完する
 * 6. 全 rows を同じソート規則で sort して返す
 *    ソートキー (V3Plans の ShiftTruck#sort_by_sort_order と同じ):
 *      truck.sortOrder → driver.sortOrder → truckId → rotationNumber
 *    固定ルート選択の有無にかかわらず同じキーを使うので、読み込み前後で既存行の並びは変わらない。
 *    固定ルートにしか存在しない placeholder 行も、truck.sortOrder が分かれば正しい位置に挿入される。
 */
export function buildVehicleRows(
  deliveries: PlanningsDeliveryWithLegRunEntity[],
  fixedRouteDetail?: ConvenienceDispatchFixedRouteDetailEntity | null,
  unassignedLegRuns: ShipmentOrderLegRunForPlanEntity[] = [],
  trucks: PlanningsTruckEntity[] = [],
  drivers: PlanningsDriverEntity[] = [],
): VehicleRowVM[] {

  // --- Step 0: 補助マップを準備 ---

  const frVehicleMap = new Map<number, ConvenienceDispatchFixedRouteVehicleEntity>();

  if (fixedRouteDetail) {
    for (const v of fixedRouteDetail.vehicles) {
      if (v.truckId == null) continue;
      frVehicleMap.set(v.truckId, v);
    }
  }

  // V3Plans 準拠のソートキー参照用
  const truckById = new Map<number, PlanningsTruckEntity>();
  for (const t of trucks) truckById.set(t.id, t);
  const driverById = new Map<number, PlanningsDriverEntity>();
  for (const d of drivers) driverById.set(d.id, d);
  const driverIdByTruckId = new Map<number, number>();
  for (const d of deliveries) {
    if (!driverIdByTruckId.has(d.truckId)) driverIdByTruckId.set(d.truckId, d.driverId);
  }

  // --- Step 0b: 固定ルート比較用インデックスを準備 ---
  // fixed route の stops を (truckId, rotation, name, kind) で高速検索できるマップ
  // - loading stop は place_name、unloading stop は store_name を使う（DB validation に合わせる）
  const getStopName = (s: ConvenienceDispatchFixedRouteStopEntity): string | null =>
    s.stopType === 'loading' ? s.placeName : s.storeName;
  const frStopIndex = new Set<string>();
  const frAllUnloadingStoreNames = new Set<string>();
  const frAllLoadingStoreNames = new Set<string>();
  if (fixedRouteDetail) {
    for (const v of fixedRouteDetail.vehicles) {
      if (v.truckId == null) continue;
      for (const s of v.stops) {
        const name = getStopName(s);
        if (!name) continue;
        frStopIndex.add(`${v.truckId}:${s.rotationNo}:${s.stopType}:${name}`);
        if (s.stopType === 'unloading') frAllUnloadingStoreNames.add(name);
        else frAllLoadingStoreNames.add(name);
      }
    }
  }

  // 全 deliveries を横断した「現在配車されている店舗名」セット (loading/unloading 別)
  const assignedUnloadingNames = new Set<string>();
  const assignedLoadingNames = new Set<string>();
  for (const d of deliveries) {
    for (const lr of d.shipmentOrderLegRuns) {
      if (lr.unloading_name) assignedUnloadingNames.add(lr.unloading_name);
      if (lr.loading_name) assignedLoadingNames.add(lr.loading_name);
    }
  }

  // 未割当案件の loading/unloading 名 セット
  const unassignedUnloadingNames = new Set<string>();
  const unassignedLoadingNames = new Set<string>();
  for (const lr of unassignedLegRuns) {
    if (lr.unloading_name) unassignedUnloadingNames.add(lr.unloading_name);
    if (lr.loading_name) unassignedLoadingNames.add(lr.loading_name);
  }

  // --- Step 1: deliveries の operations を cycle ごとに分解して row 候補を作る ---
  // 1 delivery に複数 cycle があれば複数 row 候補を生成する。

  type DeliveryRowCandidate = {
    truckId: number;
    rotationNumber: number;
    delivery: PlanningsDeliveryWithLegRunEntity;
    /** この row (cycle) に属する operations のみ */
    cycleOps: PlanningsLegRunOperationEntity[];
  };

  const deliveryRowCandidates: DeliveryRowCandidate[] = [];

  for (const d of deliveries) {
    const byCycle = new Map<number, PlanningsLegRunOperationEntity[]>();
    for (const op of d.operations) {
      const cycle = op.cycle >= 1 ? op.cycle : 1;
      if (!byCycle.has(cycle)) byCycle.set(cycle, []);
      byCycle.get(cycle).push(op);
    }

    if (byCycle.size === 0) {
      // operations が空の delivery は fallback で cycle=1 の 1 row を作る
      deliveryRowCandidates.push({ truckId: d.truckId, rotationNumber: 1, delivery: d, cycleOps: [] });
    } else {
      for (const [cycle, ops] of byCycle) {
        deliveryRowCandidates.push({ truckId: d.truckId, rotationNumber: cycle, delivery: d, cycleOps: ops });
      }
    }
  }

  // --- Step 2: fixed route detail から row 候補を作る ---

  const frRowCandidates: { truckId: number; rotationNumber: number }[] = [];
  if (fixedRouteDetail) {
    for (const v of fixedRouteDetail.vehicles) {
      if (v.truckId == null) continue;
      const rotNos = [...new Set(v.stops.map((s) => s.rotationNo))];
      for (const rn of rotNos) {
        frRowCandidates.push({ truckId: v.truckId, rotationNumber: rn });
      }
    }
  }

  // --- Step 3: (truckId, rotationNumber) の union を元に totalRotations を算出 ---

  const truckRotNos = new Map<number, Set<number>>();
  const addRotNo = (truckId: number, rotationNumber: number) => {
    if (!truckRotNos.has(truckId)) truckRotNos.set(truckId, new Set());
    truckRotNos.get(truckId).add(rotationNumber);
  };
  for (const { truckId, rotationNumber } of deliveryRowCandidates) addRotNo(truckId, rotationNumber);
  for (const { truckId, rotationNumber } of frRowCandidates) addRotNo(truckId, rotationNumber);

  const getTotalRotations = (truckId: number) => truckRotNos.get(truckId)?.size ?? 1;

  // --- Step 4a: 実配送 row を構築 ---

  const realRows: VehicleRowVM[] = deliveryRowCandidates.map(({ truckId, rotationNumber, delivery: d, cycleOps }) => {
    // この row (cycle) で使われる legRun だけを legRunMap に持つ
    const usedLegRunIds = new Set(cycleOps.map((op) => op.shipmentOrderLegRunId));
    const legRunMap = new Map<number, ShipmentOrderLegRunForPlanEntity>();
    for (const lr of d.shipmentOrderLegRuns) {
      if (lr.id != null && usedLegRunIds.has(lr.id)) legRunMap.set(lr.id, lr);
    }

    const sorted = [...cycleOps].sort((a, b) => a.arrivalAt.localeCompare(b.arrivalAt));
    const opCards = groupOpsToCards(sorted, legRunMap);

    // isRouteDeviation の再計算: 固定ルートの stops と (truckId, rotation, storeName, kind) で
    // 3+1 要素マッチを取る。一致しなければ routeDeviation=true。
    // 固定ルート未選択 (frStopIndex 空) の時は警告を出さないので false のままにする。
    if (fixedRouteDetail) {
      for (const card of opCards) {
        const stopType = card.kind === 'loading' ? 'loading' : 'unloading';
        const key = `${truckId}:${rotationNumber}:${stopType}:${card.placeName}`;
        card.isRouteDeviation = !frStopIndex.has(key);
      }
    } else {
      for (const card of opCards) {
        card.isRouteDeviation = false;
      }
    }

    // 固定ルートの各 stop に対応するカードを補完する:
    //   - legRun が配車済み → 何も出さない (該当 legRun が他車両含めどこかのカードで表示されているか、ルート外として出ている)
    //   - legRun が未割当 (unassignedUnloadingNames / unassignedLoadingNames) → 仮カード (template)
    //   - 上記いずれにも該当しない → 本日なし (no_delivery)
    //
    // 「配車済み」判定:
    //   - 降地 (unloading): 1 案件 = 1 truck の前提 → 全車合算 (assignedUnloadingNames) で判定
    //   - 積地 (loading)  : 複数 truck が同じ積地から異なる legRun を積みうる (コンビニ配送典型)
    //                       → この row の opCards だけで判定する
    const thisRowLoadingNames = new Set<string>();
    for (const op of cycleOps) {
      if (op.action !== '積') continue;
      const lr = legRunMap.get(op.shipmentOrderLegRunId);
      if (lr?.loading_name) thisRowLoadingNames.add(lr.loading_name);
    }

    const extraCards: VehicleRowCardVM[] = [];
    const frVehicle = frVehicleMap.get(truckId);
    if (frVehicle) {
      const stopsForThisRow = frVehicle.stops
        .filter((s) => s.rotationNo === rotationNumber && getStopName(s) != null)
        .sort((a, b) => a.sequenceNo - b.sequenceNo);
      for (const s of stopsForThisRow) {
        const name = getStopName(s);
        if (name == null) continue;
        const assignedForSkip = s.stopType === 'unloading' ? assignedUnloadingNames : thisRowLoadingNames;
        const unassigned = s.stopType === 'unloading' ? unassignedUnloadingNames : unassignedLoadingNames;
        if (assignedForSkip.has(name)) continue;
        if (unassigned.has(name)) {
          const matching = unassignedLegRuns.filter((lr) => {
            const lrName = s.stopType === 'unloading' ? lr.unloading_name : lr.loading_name;
            return lrName === name;
          });
          mergeOrPushExtra(extraCards, {
            kind: 'template',
            stopType: s.stopType,
            placeKey: buildFrPlaceKey(s.stopType, name, matching),
            placeName: name,
            timeLabel: null,
            itemCount: matching.reduce((sum, lr) => sum + (lr.item_count ?? 0), 0),
            operations: [],
            legRuns: matching,
            isRouteDeviation: false,
            isAutoAssigned: false,
          });
        } else {
          mergeOrPushExtra(extraCards, {
            kind: 'no_delivery',
            stopType: s.stopType,
            placeKey: buildFrPlaceKey(s.stopType, name, []),
            placeName: name,
            timeLabel: null,
            itemCount: 0,
            operations: [],
            legRuns: [],
            isRouteDeviation: false,
            isAutoAssigned: false,
          });
        }
      }
    }

    // template / no_delivery の `legRuns` は「同じ store_name の未割当 candidate」を含むため、
    // 同 row の opCards で既に実配車されている legRun が混ざると adapter で legRunIds が
    // 重複し、同 row 内に同一 lr-{id} カードが 2 枚並ぶ (React duplicate key 警告)。
    // 既に opCards に乗っている legRun は extraCards 側から除外する。
    const opLegRunIds = new Set<number>();
    opCards.forEach((c) => {
      c.legRuns.forEach((lr) => {
        if (lr.id != null) opLegRunIds.add(lr.id);
      });
    });
    const extraCardsDeduped = extraCards.map((c) => {
      if (c.legRuns.length === 0) return c;
      const filtered = c.legRuns.filter((lr) => lr.id == null || !opLegRunIds.has(lr.id));
      if (filtered.length === c.legRuns.length) return c;
      return {
        ...c,
        legRuns: filtered,
        itemCount: filtered.reduce((sum, lr) => sum + (lr.item_count ?? 0), 0),
      };
    });

    return {
      deliveryId: d.id,
      truckId,
      rotationNumber,
      totalRotations: getTotalRotations(truckId),
      driverColor: d.driverColor,
      // この cycle に属する legRun だけで集計する
      totalItemCount: [...legRunMap.values()].reduce((s, lr) => s + lr.item_count, 0),
      cards: [...opCards, ...extraCardsDeduped],
      isPlaceholderRow: false,
    };
  });

  // --- Step 4b: placeholder row を構築 (fixed route にしか存在しない行) ---

  const deliveryKeySet = new Set(deliveryRowCandidates.map((c) => `${c.truckId}:${c.rotationNumber}`));
  let placeholderIdCounter = -1;
  const placeholderRows: VehicleRowVM[] = [];

  if (fixedRouteDetail) {
    for (const { truckId, rotationNumber } of frRowCandidates) {
      if (deliveryKeySet.has(`${truckId}:${rotationNumber}`)) continue;

      const vehicle = frVehicleMap.get(truckId);
      if (!vehicle) continue;
      const placeholderCards: VehicleRowCardVM[] = [];
      const stopsForThisRow = vehicle.stops
        .filter((s) => s.rotationNo === rotationNumber && getStopName(s) != null)
        .sort((a, b) => a.sequenceNo - b.sequenceNo);
      for (const s of stopsForThisRow) {
        const name = getStopName(s);
        if (name == null) continue;
        // placeholder row は実 operation を持たないので、積地の skip 対象は無し (常に表示)。
        // 降地は他 truck で配車済みなら skip (1 案件 = 1 truck)。
        const assignedForSkip = s.stopType === 'unloading' ? assignedUnloadingNames : new Set<string>();
        const unassigned = s.stopType === 'unloading' ? unassignedUnloadingNames : unassignedLoadingNames;
        if (assignedForSkip.has(name)) continue;
        if (unassigned.has(name)) {
          const matching = unassignedLegRuns.filter((lr) => {
            const lrName = s.stopType === 'unloading' ? lr.unloading_name : lr.loading_name;
            return lrName === name;
          });
          mergeOrPushExtra(placeholderCards, {
            kind: 'template',
            stopType: s.stopType,
            placeKey: buildFrPlaceKey(s.stopType, name, matching),
            placeName: name,
            timeLabel: null,
            itemCount: matching.reduce((sum, lr) => sum + (lr.item_count ?? 0), 0),
            operations: [],
            legRuns: matching,
            isRouteDeviation: false,
            isAutoAssigned: false,
          });
        } else {
          mergeOrPushExtra(placeholderCards, {
            kind: 'no_delivery',
            stopType: s.stopType,
            placeKey: buildFrPlaceKey(s.stopType, name, []),
            placeName: name,
            timeLabel: null,
            itemCount: 0,
            operations: [],
            legRuns: [],
            isRouteDeviation: false,
            isAutoAssigned: false,
          });
        }
      }

      placeholderRows.push({
        deliveryId: placeholderIdCounter--,
        truckId,
        rotationNumber,
        totalRotations: getTotalRotations(truckId),
        driverColor: '#9e9e9e',
        totalItemCount: 0,
        cards: placeholderCards,
        isPlaceholderRow: true,
      });
    }
  }

  // --- Step 5: 全 rows を同じソート規則で sort して返す ---
  // V3Plans の ShiftTruck#sort_by_sort_order と同じ規則を用いる。
  // ソートキー:
  //   1. truck.sortOrder（未登録 truck は MAX）
  //   2. driver.sortOrder（delivery 不在の placeholder 行は MAX）
  //   3. truckId（安定化用）
  //   4. rotationNumber（1回転目 → 2回転目 → ... の順）
  const MAX = Number.MAX_SAFE_INTEGER;
  const truckSortOrderOf = (truckId: number) => truckById.get(truckId)?.sortOrder ?? MAX;
  const driverSortOrderOf = (truckId: number) => {
    const did = driverIdByTruckId.get(truckId);
    if (did == null) return MAX;
    return driverById.get(did)?.sortOrder ?? MAX;
  };

  const sorted = [...realRows, ...placeholderRows].sort((a, b) => {
    const tDiff = truckSortOrderOf(a.truckId) - truckSortOrderOf(b.truckId);
    if (tDiff !== 0) return tDiff;
    const dDiff = driverSortOrderOf(a.truckId) - driverSortOrderOf(b.truckId);
    if (dDiff !== 0) return dDiff;
    if (a.truckId !== b.truckId) return a.truckId - b.truckId;
    return a.rotationNumber - b.rotationNumber;
  });

  // --- Step 6: 各 truck の rotation 番号を 1..N に振り直す（繰り上げ）---
  // op.cycle ベースのままだと、回転途中を削除したときに "回転3" が残って
  // "回転2" がない歯抜け表示になる。サンプル仕様 (回転3 → 回転2) に合わせて
  // 表示用に sourceRowsRaw 段階で連番を振り直す。
  // 注: row.rotationNumber は後段の (a) card ID 生成 (b) timeline_operations の cycle
  // にも使われるので、両方とも連番ベースになる。次回の auto-dispatch 時に backend の
  // op.cycle が連番値で上書きされて自然に整合する。
  const totalsByTruck = new Map<number, number>();
  const seqByTruck = new Map<number, number>();
  for (const row of sorted) {
    totalsByTruck.set(row.truckId, (totalsByTruck.get(row.truckId) ?? 0) + 1);
  }
  return sorted.map((row) => {
    const seq = (seqByTruck.get(row.truckId) ?? 0) + 1;
    seqByTruck.set(row.truckId, seq);
    return {
      ...row,
      rotationNumber: seq,
      totalRotations: totalsByTruck.get(row.truckId) ?? row.totalRotations,
    };
  });
}
